/**
 * Vertical taxonomy for the client.
 *
 * The backend registry (`backend/src/core/verticals/`) is the source of truth —
 * it drives competitor discovery, market math and prompt wording. These mirrors
 * exist so pickers and copy render without a round-trip, and are validated
 * against the backend by `GET /agency/verticals`.
 *
 * Keep `VERTICAL_SPECIALTIES` in sync with the backend profiles. The Command
 * Center fetches the live list where correctness matters; this is the fallback
 * and the type source.
 */

export type VerticalId = 'dental' | 'medical';

export interface SpecialtyOption {
  value: string;
  label: string;
}

/** Modules whose content is authored per vertical rather than shared. */
export interface VerticalModules {
  /**
   * Safety & Compliance ships dental-clinical OSHA/BBP training. Verticals
   * without a training pack hide the module rather than show the wrong content.
   */
  safety: boolean;
}

/**
 * One forced-choice brand-archetype question.
 *
 * Mirrors VerticalBrandQuestion in backend/src/core/verticals/types.ts. The
 * order, questionKeys and option polarity are load-bearing — brandArchetype.ts
 * scores the first four as Axis A (option 0 = Structure) and the rest as Axis B
 * (option 1 = Mastery). Reword freely; do not reorder or flip.
 */
export interface BrandQuestion {
  axis: 'A' | 'B';
  questionKey: string;
  q: string;
  opts: readonly [string, string];
}

export interface VerticalOption {
  id: VerticalId;
  label: string;
  generalistSpecialty: string;
  specialties: SpecialtyOption[];
  modules: VerticalModules;
  /** Staff roles offered, with vertical-appropriate labels. */
  staffRoles: { value: string; label: string }[];
  /**
   * What this industry calls its practice-management software — "PMS" in
   * dentistry, "EHR" in medicine. Used in upload instructions and the
   * "Connect directly" card.
   */
  practiceSoftwareAbbreviation: string;
  /** Named systems, shown as examples of what the direct connector will support. */
  practiceSoftware: string[];
  brandQuestions: readonly BrandQuestion[];
}

const DENTAL_BRAND_QUESTIONS: readonly BrandQuestion[] = [
  {
    axis: 'A',
    questionKey: 'brand_archetype_q1',
    q: 'When a patient is anxious, you\u2026',
    opts: [
      'Steady them with a calm, proven routine',
      'Improvise \u2014 whatever helps in the moment',
    ],
  },
  {
    axis: 'A',
    questionKey: 'brand_archetype_q2',
    q: 'Your ideal office runs\u2026',
    opts: ['Orderly and dependable', 'Loose and easygoing'],
  },
  {
    axis: 'A',
    questionKey: 'brand_archetype_q3',
    q: "You'd rather be known for\u2026",
    opts: ['Consistency patients count on', 'Doing dentistry differently'],
  },
  {
    axis: 'A',
    questionKey: 'brand_archetype_q4',
    q: 'New treatments and tech?',
    opts: ["Adopt once they're well-proven", "Love being early to what's new"],
  },
  {
    axis: 'B',
    questionKey: 'brand_archetype_q5',
    q: "Patients should choose you because you're\u2026",
    opts: ['Part of the community, like family', 'The most capable, expert choice'],
  },
  {
    axis: 'B',
    questionKey: 'brand_archetype_q6',
    q: 'Your voice is\u2026',
    opts: ['Down-to-earth and personal', 'Authoritative and expert'],
  },
  {
    axis: 'B',
    questionKey: 'brand_archetype_q7',
    q: 'The patients you most want\u2026',
    opts: ['Whole families who stay for years', "The hard cases others can't solve"],
  },
  {
    axis: 'B',
    questionKey: 'brand_archetype_q8',
    q: 'Success looks like\u2026',
    opts: ['Lifelong neighborhood relationships', 'Recognized mastery of complex care'],
  },
];

const MEDICAL_BRAND_QUESTIONS: readonly BrandQuestion[] = [
  {
    axis: 'A',
    questionKey: 'brand_archetype_q1',
    q: 'When a patient is anxious, you\u2026',
    opts: [
      'Steady them with a calm, proven routine',
      'Improvise \u2014 whatever helps in the moment',
    ],
  },
  {
    axis: 'A',
    questionKey: 'brand_archetype_q2',
    q: 'Your ideal clinic runs\u2026',
    opts: ['Orderly and dependable', 'Loose and easygoing'],
  },
  {
    axis: 'A',
    questionKey: 'brand_archetype_q3',
    q: "You'd rather be known for\u2026",
    opts: ['Consistency patients count on', 'Practising medicine differently'],
  },
  {
    axis: 'A',
    questionKey: 'brand_archetype_q4',
    q: 'New treatments and technology?',
    opts: ['Adopt once the evidence is strong', "Love being early to what's new"],
  },
  {
    axis: 'B',
    questionKey: 'brand_archetype_q5',
    q: "Patients should choose you because you're\u2026",
    opts: ['A trusted part of the community', 'The most capable, expert choice'],
  },
  {
    axis: 'B',
    questionKey: 'brand_archetype_q6',
    q: 'Your voice is\u2026',
    opts: ['Down-to-earth and personal', 'Authoritative and expert'],
  },
  {
    axis: 'B',
    questionKey: 'brand_archetype_q7',
    q: 'The patients you most want\u2026',
    opts: ['Patients you follow for years', 'The complex cases others refer on'],
  },
  {
    axis: 'B',
    questionKey: 'brand_archetype_q8',
    q: 'Success looks like\u2026',
    opts: [
      'Lasting trust with patients and referring providers',
      'Recognized mastery of complex care',
    ],
  },
];

/** Practices created before multi-vertical support are dental. */
export const DEFAULT_VERTICAL: VerticalId = 'dental';

export const VERTICALS: Record<VerticalId, VerticalOption> = {
  dental: {
    id: 'dental',
    label: 'Dental',
    generalistSpecialty: 'general_dentistry',
    specialties: [
      { value: 'general_dentistry', label: 'General Dentistry' },
      { value: 'cosmetic_dentistry', label: 'Cosmetic Dentistry' },
      { value: 'orthodontics', label: 'Orthodontics' },
      { value: 'pediatric_dentistry', label: 'Pediatric Dentistry' },
      { value: 'periodontics', label: 'Periodontics' },
      { value: 'endodontics', label: 'Endodontics' },
      { value: 'oral_surgery', label: 'Oral Surgery' },
      { value: 'prosthodontics', label: 'Prosthodontics' },
      { value: 'implant_dentistry', label: 'Implant Dentistry' },
      { value: 'emergency_dentistry', label: 'Emergency Dentistry' },
      { value: 'sleep_dentistry', label: 'Sleep Dentistry' },
      { value: 'holistic_dentistry', label: 'Holistic Dentistry' },
      { value: 'multi_specialty', label: 'Multi-Specialty' },
      { value: 'other', label: 'Other' },
    ],
    modules: { safety: true },
    practiceSoftwareAbbreviation: 'PMS',
    practiceSoftware: ['Dentrix', 'Open Dental', 'Eaglesoft', 'Curve', 'Oryx'],
    brandQuestions: DENTAL_BRAND_QUESTIONS,
    staffRoles: [
      { value: 'provider', label: 'Provider / Dentist' },
      { value: 'hygienist', label: 'Hygienist' },
      { value: 'assistant', label: 'Dental Assistant' },
      { value: 'sterilization_tech', label: 'Sterilization Tech' },
      { value: 'clinical_team_manager', label: 'Clinical Team Manager' },
      { value: 'front_desk', label: 'Front Desk' },
      { value: 'office_manager', label: 'Office Manager' },
    ],
  },
  medical: {
    id: 'medical',
    label: 'Medical',
    generalistSpecialty: 'primary_care',
    specialties: [
      { value: 'primary_care', label: 'Primary Care / Internal Medicine' },
      { value: 'ophthalmology', label: 'Ophthalmology' },
      { value: 'cardiology', label: 'Cardiology' },
      { value: 'dermatology', label: 'Dermatology' },
      { value: 'orthopedics', label: 'Orthopedics' },
      { value: 'otolaryngology', label: 'ENT / Otolaryngology' },
      { value: 'gastroenterology', label: 'Gastroenterology' },
      { value: 'urology', label: 'Urology' },
      { value: 'neurology', label: 'Neurology' },
      { value: 'obgyn', label: 'OB/GYN' },
      { value: 'pediatrics', label: 'Pediatrics' },
      { value: 'multi_specialty', label: 'Multi-Specialty' },
      { value: 'other', label: 'Other' },
    ],
    modules: { safety: false },
    practiceSoftwareAbbreviation: 'EHR',
    practiceSoftware: ['Epic', 'athenahealth', 'eClinicalWorks', 'NextGen', 'Oracle Health'],
    brandQuestions: MEDICAL_BRAND_QUESTIONS,
    // No hygienist analogue in medicine — the role is simply not offered.
    staffRoles: [
      { value: 'provider', label: 'Physician / Provider' },
      { value: 'assistant', label: 'Medical Assistant / Nurse' },
      { value: 'front_desk', label: 'Front Desk' },
      { value: 'office_manager', label: 'Practice Manager' },
    ],
  },
};

export const VERTICAL_OPTIONS: SpecialtyOption[] = Object.values(VERTICALS).map((v) => ({
  value: v.id,
  label: v.label,
}));

export function isVerticalId(value: string | null | undefined): value is VerticalId {
  return value != null && value in VERTICALS;
}

export function getVertical(vertical: string | null | undefined): VerticalOption {
  return isVerticalId(vertical) ? VERTICALS[vertical] : VERTICALS[DEFAULT_VERTICAL];
}

/** Specialty picker options for a vertical, with the empty prompt row prepended. */
export function specialtyOptions(vertical: string | null | undefined): SpecialtyOption[] {
  return [{ value: '', label: 'Select specialty...' }, ...getVertical(vertical).specialties];
}

/**
 * Human label for a specialty. Falls back across verticals when the caller has
 * no vertical to hand — practice rows loaded before the field was threaded
 * through still need to render.
 */
export function getSpecialtyLabel(
  value: string | null | undefined,
  vertical?: string | null,
): string {
  if (!value) return '';
  if (vertical) {
    const match = getVertical(vertical).specialties.find((s) => s.value === value);
    if (match) return match.label;
  }
  for (const v of Object.values(VERTICALS)) {
    const match = v.specialties.find((s) => s.value === value);
    if (match) return match.label;
  }
  return value;
}

export function getVerticalLabel(vertical: string | null | undefined): string {
  return getVertical(vertical).label;
}

export function isGeneralistSpecialty(
  vertical: string | null | undefined,
  specialty: string | null | undefined,
): boolean {
  const v = getVertical(vertical);
  return (specialty || v.generalistSpecialty) === v.generalistSpecialty;
}

/**
 * The practice's type as one display string — "Medical · Cardiology".
 *
 * Vertical is shown alongside specialty rather than instead of it: the specialty
 * alone is ambiguous across industries ("Pediatrics" and "Multi-Specialty" exist
 * in both), and the vertical is what actually drives competitor discovery,
 * benchmarks and generated copy, so it is the part an agency user needs to be
 * able to confirm at a glance.
 *
 * Use this everywhere a practice's type is displayed so the four surfaces
 * (Command Center rows, Practices table, practice header, Practice Information)
 * cannot drift apart.
 */
export function practiceTypeLabel(
  vertical: string | null | undefined,
  specialty: string | null | undefined,
): string {
  const verticalLabel = getVerticalLabel(vertical);
  const specialtyText = getSpecialtyLabel(specialty, vertical);
  return specialtyText ? `${verticalLabel} · ${specialtyText}` : verticalLabel;
}

/** Brand-archetype questions worded for a vertical. Keys and order never vary. */
export function brandQuestionsFor(vertical: string | null | undefined): readonly BrandQuestion[] {
  return getVertical(vertical).brandQuestions;
}
